import { useMemo, useRef, useState } from 'react'
import { Row, Col, Card } from 'react-bootstrap'
import { ICustomColumn, IPageProps } from 'types'
import { PageHeader } from 'components'
import { V1ApiReportsClient } from 'core/http-common'
import toast from 'core/toast'
import { TestDriveRewardsSalesDto, TestDriveRewardsSalesTotalsDto } from '@v1ReportsApi'
import SAGrid, { GetDataResponse, GridGetDataProps, ISAGridRef } from 'components/Grid/SAGrid'
import { Skeleton } from '@progress/kendo-react-indicators'
import { startLoadingAction, endLoadingAction } from 'state/actions/appActions'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'state/store'

interface ITestDriveRewardsTier {
    field: 'testDrive149' | 'testDrive199' | 'testDrive249'
    totalsField: keyof TestDriveRewardsSalesTotalsDto
    label: string
    price: number
}

const TIERS: ITestDriveRewardsTier[] = [
    { field: 'testDrive149', totalsField: 'testDrive149Count', label: 'Test Drive Rewards $149', price: 149 },
    { field: 'testDrive199', totalsField: 'testDrive199Count', label: 'Test Drive Rewards $199', price: 199 },
    { field: 'testDrive249', totalsField: 'testDrive249Count', label: 'Test Drive Rewards $249', price: 249 },
]

const gridLocalStorageNames = {
    sort: 'testDriveRewardsReportSort',
    search: 'testDriveRewardsReportSearch',
    selectedPage: 'testDriveRewardsReportSelectedPage',
    pageSize: 'testDriveRewardsReportPageSize',
}

const TestDriveRewardsSalesPage = ({ title }: IPageProps) => {

    const dispatch = useDispatch<AppDispatch>()
    const gridRef = useRef<ISAGridRef>(null)
    const [loaded, setLoaded] = useState(false)
    const [serverTotals, setServerTotals] = useState<TestDriveRewardsSalesTotalsDto | undefined>(undefined)

    const columns: ICustomColumn[] = [
        { field: 'dealerId', title: 'Dealer ID', filterType: 'numeric', width: '110px' },
        { field: 'dealerName', title: 'Dealer Name' },
        { field: 'dateEnrolled', title: 'Date Enrolled', format: '{0:d}', filterType: 'date', width: '145px' },
        { field: 'address1', title: 'Address 1', width: '180px' },
        { field: 'testDrive149', title: 'Test Drive Rewards $149', filterType: 'numeric', width: '190px' },
        { field: 'testDrive199', title: 'Test Drive Rewards $199', filterType: 'numeric', width: '190px' },
        { field: 'testDrive249', title: 'Test Drive Rewards $249', filterType: 'numeric', width: '190px' },
    ]


    const fetchGridData = async (props?: GridGetDataProps): Promise<GetDataResponse> => {
        try {
            await dispatch(startLoadingAction())

            const pageSize = props?.paging?.take ?? 25
            const page = Math.floor((props?.paging?.skip ?? 0) / pageSize) + 1

            const result = await V1ApiReportsClient.getTestDriveRewardsSalesReport(
                props?.search || undefined,
                props?.sortBy,
                props?.sortAscending,
                page,
                pageSize,
            )

            setServerTotals(result?.totals)

            return {
                data: result?.data ?? [],
                total: Number(result?.totalItems ?? 0),
            }
        } catch (error) {
            toast.responseErrors(error)
            return { data: [], total: 0 }
        } finally {
            setLoaded(true)
            await dispatch(endLoadingAction())
        }
    }

    const totals = useMemo(() => {
        const tierTotals = TIERS.map(tier => {
            const count = serverTotals?.[tier.totalsField] as number ?? 0
            return { ...tier, count, subtotal: count * tier.price }
        })

        const totalCount = tierTotals.reduce((sum, tier) => sum + tier.count, 0)
        const totalAmount = tierTotals.reduce((sum, tier) => sum + tier.subtotal, 0)

        return { tierTotals, totalCount, totalAmount }
    }, [serverTotals])

    const currencyFormat = (value: number) => value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })

    return (
        <div id='TestDriveRewardsSalesPage'>
            <PageHeader title={title} />

            {!loaded ?
                <Skeleton shape='rectangle' style={{ width: '100%', height: '120px' }} animation={{ type: 'pulse' }} />
                :
                <Row className='mb-3'>
                    <Col md={3} className='mb-2'>
                        <Card className='bg-light border rounded shadow-sm h-100'>
                            <Card.Body>
                                <Card.Title className='fs-6 text-muted mb-1'>Total Enrolled / Total $</Card.Title>
                                <Card.Text className='fs-4 mb-0'>
                                    {totals.totalCount} / {currencyFormat(totals.totalAmount)}
                                </Card.Text>
                            </Card.Body>
                        </Card>
                    </Col>
                    {totals.tierTotals.map(tier => (
                        <Col md={3} className='mb-2' key={tier.field}>
                            <Card className='bg-light border rounded shadow-sm h-100'>
                                <Card.Body>
                                    <Card.Title className='fs-6 text-muted mb-1'>{tier.label}</Card.Title>
                                    <Card.Text className='fs-4 mb-0'>
                                        {tier.count} / {currencyFormat(tier.subtotal)}
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            }

            <SAGrid
                ref={gridRef}
                columns={columns}
                getData={fetchGridData}
                defaultSort={[{ field: 'dealerId', dir: 'asc' }]}
                features={{
                    pageable: true,
                    sortable: true,
                    exportToExcel: true,
                    reorderColumns: true,
                    simpleSearch: true,
                    resizable: true,
                    title: 'Test Drive Rewards',
                }}
                localStorageNames={gridLocalStorageNames}
            />
        </div>
    )
}
export default TestDriveRewardsSalesPage
